import { SyntheticEvent, useContext, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import Tab from "@mui/material/Tab";

import {
    BannerType,
    BannerView,
    colors,
    MergeTypeIcon,
    MoveFilesIcon,
    NavigationArrowRightRoundedIcon
} from "@dotnet/telematics-design-system";

import Dialog from "components/DesignComponents/Dialog";
import DialogConfirmation from "components/DesignComponents/DialogConfirmation";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import SegmentedToggle, {
    TAB_SIZES
} from "components/DesignComponents/SegmentedToggle";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import NoPermission from "components/TableEmpty/NoPermission";
import AlertContext from "context/alert/alertContext";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import {
    COMPANY_TYPES,
    ENDPOINTS,
    isAllowedToEditCompany,
    isClientTypeCompany,
    isInternalTypeCompany,
    Query,
    svgFontSize,
    TABLE_NAMES,
    useApi,
    usePermissions
} from "shared";

import { COMPANY_ACTIONS, DialogProps } from "../../../types";
import DialogActions from "../../components/DialogActions";
import CompanyStats from "./CompanyStats";
import {
    PARTIES,
    TRANSFER_COMPANY_ACTIONS,
    TransferDropdownData
} from "./types";

const TransferCompanies = ({ isOpen, close }: DialogProps) => {
    const { gray700 } = colors;

    const { removeAlertForBGAction, setBackgroundActionId } =
        useContext(AlertContext);

    const { triggerRootCompany } = useContext(ParamsContext);
    const { user } = useContext(AuthContext);
    const { rows } = useContext(TableContext);

    const { t } = useTranslation();
    const { updateData, postData, handleResponse } = useApi();

    const { isUpdateCompanyNotAllowed, isMergeCompanyNotAllowed } =
        usePermissions();

    const [isShowConfirmation, setIsShowConfirmation] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tabValue, setTabValue] = useState(TRANSFER_COMPANY_ACTIONS.Move);

    const [company, setCompany] = useState<TransferDropdownData>({
        [PARTIES.Source]: null,
        [PARTIES.Target]: null
    });

    const { source, target } = company;

    const isReady = source && target;

    const isMoveAction = tabValue === TRANSFER_COMPANY_ACTIONS.Move;
    const isMergeAction = tabValue === TRANSFER_COMPANY_ACTIONS.Merge;

    const isMergeLoading = isMergeAction && isLoading;

    const isMoveActionDisabled = isMoveAction && isUpdateCompanyNotAllowed;
    const isMergeActionDisabled = isMergeAction && isMergeCompanyNotAllowed;
    const isNoPermission = isMoveActionDisabled || isMergeActionDisabled;

    // non-primitive value as a dependency, it is memoized to avoid re-rendering
    const sourceExceptions = useMemo(() => {
        return [
            ...(user?.company_id ? [user.company_id] : []),
            ...(target?.id ? [target.id] : [])
        ];
    }, [target?.id, user?.company_id]);

    const setTarget = (sourceData: Query | null) =>
        sourceData?.companyInfo?.parentCompany || null;

    const handleTabChange = (
        _: SyntheticEvent,
        newValue: TRANSFER_COMPANY_ACTIONS
    ) => {
        setTabValue(newValue);

        const isMergeTab = newValue === TRANSFER_COMPANY_ACTIONS.Merge;

        if (isMergeTab && !isMergeCompanyNotAllowed) {
            if (
                isClientTypeCompany(source?.companyInfo?.companyType) ||
                hasCode(source?.companyInfo?.lzCode)
            ) {
                setCompany({
                    [PARTIES.Source]: null,
                    [PARTIES.Target]: null
                });
            } else {
                setCompany({
                    ...company,
                    [PARTIES.Target]: setTarget(source)
                });
            }
        }
    };

    const handleDropdownChange = (name: PARTIES, value: Query | null) => {
        const isSource = name === PARTIES.Source;
        const isEmpty = value === null;

        const isSourceInternalType = isInternalTypeCompany(
            value?.companyInfo?.companyType
        );

        if (isMoveAction) {
            const isTargetNotInternal = !isInternalTypeCompany(
                target?.companyInfo?.companyType
            );

            if (
                isSource &&
                (isEmpty || (isSourceInternalType && isTargetNotInternal))
            ) {
                setCompany({
                    [PARTIES.Source]: value,
                    [PARTIES.Target]: null
                });
            } else {
                setCompany({
                    ...company,
                    [name]: value
                });
            }
        } else {
            setCompany({
                [PARTIES.Source]: value,
                [PARTIES.Target]: setTarget(value)
            });
        }
    };

    const handleCloseDialog = () => close(COMPANY_ACTIONS.TransferCompanies);
    const handleOpenDialogConfirmation = () => setIsShowConfirmation(true);
    const handleCloseDialogConfirmation = () => setIsShowConfirmation(false);

    const handleSubmit = async () => {
        removeAlertForBGAction();

        if (isReady) {
            handleCloseDialogConfirmation();

            const isSourceOrTargetInTable = rows.some(
                company => company.id === source.id || company.id === target.id
            );

            try {
                setIsLoading(true);

                const payload = isMoveAction
                    ? { company_id: target.id }
                    : {
                          source: source.id,
                          target: target.id
                      };

                const response = isMoveAction
                    ? await updateData(ENDPOINTS.Companies, source.id, payload)
                    : await postData(`${ENDPOINTS.Companies}/merge`, payload);

                if (isMoveAction) {
                    handleCloseDialog();
                    triggerRootCompany(isSourceOrTargetInTable);

                    handleResponse(response);
                } else {
                    setBackgroundActionId(
                        response.data,
                        TABLE_NAMES.Companies,
                        () => {
                            handleCloseDialog();
                            triggerRootCompany(isSourceOrTargetInTable);
                        },
                        () => setIsLoading(false)
                    );
                }
            } catch (error) {
                handleResponse(error);
                setIsLoading(false);
            }
        }
    };

    const getDescription = () => {
        const companyNames = {
            sourceCompany: `"${source?.name}"`,
            targetCompany: `"${target?.name}"`
        };

        const firstPart = isMoveAction
            ? t(
                  "Dialog##confirmations##move companies confirmation",
                  companyNames
              )
            : t(
                  "Dialog##confirmations##merge companies confirmation",
                  companyNames
              );

        const secondPart = t(
            "Dialog##confirmations##are you sure you wish to continue"
        );

        return `${firstPart} ${secondPart}`;
    };

    const hasCode = (code: number | string | null | undefined) =>
        code !== null && code !== "" && code !== undefined;

    const isSourceClientDisabled = (companyType?: COMPANY_TYPES) =>
        isMergeAction && isClientTypeCompany(companyType);

    const isInternalDisabled = (companyType?: COMPANY_TYPES) =>
        !isAllowedToEditCompany(user!.access_level, companyType);

    const isLzCodeDisabled = (code: number | string | null | undefined) =>
        isMergeAction && hasCode(code);

    const getSourceDisabledOptionTooltip = (option: Query) => {
        if (isSourceClientDisabled(option.companyInfo?.companyType)) {
            return t(
                "Dialog##merge companies disabled option##client to internal"
            );
        }

        if (isInternalDisabled(option.companyInfo?.companyType)) {
            return t(
                `Dialog##${isMoveAction ? "move" : "merge"} companies disabled option##internal`
            );
        }

        if (isLzCodeDisabled(option.companyInfo?.lzCode)) {
            return t("Dialog##merge companies disabled option##lz code");
        }

        return null;
    };

    return (
        <>
            <Dialog
                title={t("Dialog##transfer companies")}
                description={t("Dialog##transfer companies description")}
                isTitleSeparator
                isActionsSeparator
                open={isOpen}
                close={isMergeLoading ? undefined : handleCloseDialog}
                extendToMaxWidth
                submit={handleOpenDialogConfirmation}
                slotProps={{
                    transition: {
                        onExited: () => {
                            removeAlertForBGAction();
                            setIsLoading(false);
                            setTabValue(TRANSFER_COMPANY_ACTIONS.Move);

                            setCompany({
                                [PARTIES.Source]: null,
                                [PARTIES.Target]: null
                            });
                        }
                    }
                }}
                actions={
                    <DialogActions
                        onClose={handleCloseDialog}
                        isLoading={isMergeLoading || isLoading}
                        loaderText={
                            isMergeAction ? t("Button##merging") : undefined
                        }
                        isReadyToConfirm={isReady}
                        confirmationButtonText={
                            isMoveAction
                                ? t("Button##move")
                                : t("Button##merge")
                        }
                        cancelButtonTestId="cancel-create-task-button"
                        confirmationButtonTestId="confirm-create-task-button"
                    />
                }
            >
                <SegmentedToggle
                    value={tabValue}
                    size={TAB_SIZES.Small}
                    fullWidth
                    onChange={handleTabChange}
                    css={css({
                        marginBottom: "16px"
                    })}
                >
                    <Tab
                        label={t("Button##move")}
                        icon={<MoveFilesIcon />}
                        disabled={isLoading}
                    />

                    <Tab
                        label={t("Button##merge")}
                        icon={<MergeTypeIcon />}
                        disabled={isLoading}
                    />
                </SegmentedToggle>

                {isNoPermission ? (
                    <NoPermission />
                ) : (
                    <>
                        {isMergeAction && (
                            <BannerView
                                data-testid="banner-message"
                                bannerType={BannerType.Info}
                                title={t("Dialog##merge companies info")}
                                css={css({
                                    marginBottom: "16px"
                                })}
                            />
                        )}

                        <div
                            css={css({
                                display: "flex",
                                alignItems: "center"
                            })}
                        >
                            <CompanyDropdown
                                key={`${tabValue}-source`}
                                data-testid="source-company-query-dropdown"
                                value={source}
                                getOptionDisabled={(option: Query) =>
                                    isSourceClientDisabled(
                                        option.companyInfo?.companyType
                                    ) ||
                                    isInternalDisabled(
                                        option.companyInfo?.companyType
                                    ) ||
                                    isLzCodeDisabled(option.companyInfo?.lzCode)
                                }
                                getOptionDisabledTooltip={option =>
                                    getSourceDisabledOptionTooltip(option)
                                }
                                change={(_, val) =>
                                    handleDropdownChange(PARTIES.Source, val)
                                }
                                labelLeft={t("Table##source company")}
                                readOnly={isLoading}
                                exceptions={sourceExceptions}
                            />

                            <NavigationArrowRightRoundedIcon
                                css={css({
                                    color: gray700,
                                    margin: "24px 8px 0 8px",
                                    flexShrink: "0",
                                    ...svgFontSize(16)
                                })}
                            />

                            {isMoveAction ? (
                                <CompanyDropdown
                                    key={`${tabValue}-target`}
                                    data-testid="target-company-query-dropdown"
                                    value={target}
                                    change={(_, val) =>
                                        handleDropdownChange(
                                            PARTIES.Target,
                                            val
                                        )
                                    }
                                    labelLeft={t("Table##target company")}
                                    disabled={source === null}
                                    readOnly={isLoading}
                                    exceptions={source?.id}
                                    hasOnlyInternalCompanies={isInternalTypeCompany(
                                        source?.companyInfo?.companyType
                                    )}
                                />
                            ) : (
                                <InputField
                                    data-testid="target-company-input-field"
                                    size={INPUT_FIELD_SIZES.Medium}
                                    fullWidth
                                    readOnly
                                    labelLeft={t("Table##target company")}
                                    value={target?.name || ""}
                                />
                            )}
                        </div>

                        <CompanyStats
                            source={source}
                            target={target}
                            isMoveAction={isMoveAction}
                        />
                    </>
                )}
            </Dialog>

            <DialogConfirmation
                isOpen={isShowConfirmation}
                description={getDescription()}
                onClose={handleCloseDialogConfirmation}
                onSubmit={handleSubmit}
                confirmBtnTitle={t("Button##yes")}
            >
                {isMergeAction && (
                    <BannerView
                        bannerType={BannerType.Warning}
                        title={t("Dialog##confirm cannot revert")}
                        css={css({
                            margin: "8px 0"
                        })}
                    />
                )}
            </DialogConfirmation>
        </>
    );
};

export default TransferCompanies;
